/**
 * ClubDirectory — main /club page.
 *
 * Renders: hero · search · filters · grid · detail modal · request modal · confirm.
 * Wires to /api/club/* with the founder member (FOUNDER-LC-001) in gate-off mode.
 *
 * FIR-20260611-001 · Phase 3 (V1 build)
 */

import React, { useEffect, useMemo, useState } from "react";
import ClubLayout from "./ClubLayout";
import ClubGate from "./ClubGate";
import { AMENITIES, TYPES, SERVICE_FORMS } from "./serviceForms";
import * as api from "./api";
import ConciergeWhatsAppPill from "@/components/global/ConciergeWhatsAppPill";
import CldImg from "@/components/CldImg";
import { DrawerVoiceLine } from "@/components/shared/DrawerAtmosphere";
import {
  Sailboat, Anchor, Palmtree, Camera, Plane, Mountain,
  Sparkles, Wine, Utensils, Trophy, Target, Fish, Car,
  Building2, Users, BookOpen, Palette, Hotel, Coffee, Flag,
} from "lucide-react";

// FIR-20260625 · Per-Type icon instead of letter monogram for clubs
// without a hero image. Lucide-react is already in package.json.
const TYPE_ICON = {
  "Beach Club":                            Palmtree,
  "Yacht Club":                            Sailboat,
  "Yacht & Sailing":                       Sailboat,
  "Sailing Club":                          Sailboat,
  "Wellness, Bathhouse & Recovery Club":   Sparkles,
  "Equestrian Club":                       Trophy,
  "Turf & Equestrian":                     Trophy,
  "Flying Club":                           Plane,
  "Adventure & Outdoor Club":              Mountain,
  "Art & Design Club":                     Palette,
  "Photography Club":                      Camera,
  "Food & Wine Club":                      Wine,
  "Fishing Club":                          Fish,
  "Motorsport & Driving Club":             Car,
  "Shooting Club":                         Target,
  "Business, Founder & Innovation Club":   Building2,
  "Racquet & Social Sports Club":          Trophy,
  "Dining":                                Utensils,
  "Accommodation":                         Hotel,
  "City & Social":                         Coffee,
  "Country Club":                          Anchor,
  "Golf":                                  Flag,
  "Gymkhana":                              Users,
  "Sports & Recreation":                   Trophy,
  "Cosmopolitan":                          BookOpen,
};
function IconForType({ type, size = 56 }) {
  const Ico = TYPE_ICON[type] || Anchor;
  return <Ico size={size} strokeWidth={1.4} style={{ color: "rgba(255,255,255,0.85)" }} />;
}

const PAGE_SIZE = 48;
const SESSION_KEY = "club_member_session_v1";

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveSession(member) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(member)); } catch { /* ignore */ }
}
function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

function monogram(name) {
  if (!name) return "C";
  return (name.replace(/^The\s+/i, "")[0] || "C").toUpperCase();
}

function locOf(c) {
  const sec = c.country === "India" ? (c.state || c.country) : c.country;
  return `${c.city || ""}${sec ? ", " + sec : ""}`;
}

function partyTotal(p) {
  return 1 + (p.spouse ? 1 : 0) + (p.children || 0);
}

/* ─── Card ────────────────────────────────────────────── */
function ClubCard({ club, onOpen }) {
  const at = (club.am || []).slice(0, 4);
  const more = (club.am || []).length - at.length;
  return (
    <div
      className="cardc"
      data-testid={`club-card-${club.id_short}`}
      onClick={() => onOpen(club.id_short)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(club.id_short); }}
    >
      <div className="cimg">
        <div className="cmono" data-testid={`club-card-icon-${club.id_short}`}>
          <IconForType type={club.type} />
        </div>
        {club.img && (
          <CldImg src={club.img} w={720} alt={club.name}
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        {club.type && <span className="ctag">{club.type}</span>}
      </div>
      <div className="cbody">
        <h3 className="serif">{club.name}</h3>
        <div className="cloc">◦ {locOf(club)}</div>
        {club.short && <div className="cdesc">{club.short}</div>}
        <div className="cam">
          {at.map((a) => <span className="amtag" key={a}>{a}</span>)}
          {more > 0 && <span className="amtag more">+{more}</span>}
        </div>
      </div>
    </div>
  );
}

/* ─── Long-description formatter ──────────────────────── */
//
// Source data lives in `lc_clubs.long` (or falls back to `short`) and
// follows a consistent shape on the founder-curated catalogue:
//
//   <one or more lead paragraphs separated by blank lines>
//   \n\nImportant Access Note\n
//   * bullet one
//   * bullet two
//   * ...
//
// FIR-20260218-AccessNote · founder ruling 18 Feb 2026: the per-row
// "Important Access Note" copy varies slightly across 3,506 rows. To
// guarantee a single source of truth and consistent legal posture, we
// IGNORE the per-row access-note text at render-time and emit a
// CANONICAL access note from this file — one version for Golf clubs
// (with the Handicap Index paragraph), one for all other clubs. The
// per-row prose still drives the lead paragraph(s) above it.
function formatLongDescription(raw) {
  if (!raw) return null;
  const text = String(raw).replace(/\r\n/g, "\n").trim();
  // Split on the access-note heading so we only keep the LEAD prose.
  // The bullets below it are ignored (replaced by canonical note).
  const m = text.split(/\n\s*important access note\s*:?\s*\n/i);
  const leadRaw = (m[0] || "").trim();
  const leadParas = leadRaw.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  return (
    <>
      {leadParas.map((p, i) => (
        <p className="lead" key={`lead-${i}`}>{p}</p>
      ))}
    </>
  );
}

/* ─── Canonical Important Access Note · founder-locked 18 Feb 2026 ───
 * Two canonical versions · selected by club.type === "Golf". Replaces
 * the per-row bullets that lived inline in `lc_clubs.long`. Single
 * source of truth — when legal wants a change, edit it here, every
 * club inherits it on the next deploy.
 */
function CanonicalAccessNote({ isGolf }) {
  // Founder-locked 23 Jun 2026 · single canonical version applied to
  // every club (golf clubs no longer get a separate variant — the
  // Handicap rule is now a universal bullet so members are reminded of
  // it on every listing). Rendered as a real <ul> for scannability.
  // Each <li> is a distinct legal point.
  // (`isGolf` is accepted for backwards compatibility but no longer
  // toggles content — both `access-note-golf` and `access-note-default`
  // testids continue to work so existing tests don't break.)
  const bullets = [
    <>MindEscapes<sup>®</sup> operates a directory and Concierge<sup>®</sup> service that helps its members discover venues and facilitate requests, acting solely as an intermediary and facilitator.</>,
    <>This listing is independently compiled and maintained for informational purposes only; inclusion of any venue is indicative and is not a representation, warranty, endorsement, affiliation, association, partnership or sponsorship, nor a claim of any arrangement with that venue or a guarantee that access will be available.</>,
    <>Listings may be added, paused, amended or removed at any time without notice.</>,
    <>Access to any venue is made available through one or more of MindEscapes<sup>®</sup>&apos;s own reciprocal or partner arrangements, partner-club networks, supplier or third-party arrangements, or because select amenities of the venue are open to guests — and MindEscapes<sup>®</sup> does not represent that it has a direct affiliation, partnership or association with every venue listed.</>,
    <>All access is at the host venue&apos;s discretion and subject to its rules and availability on the day; reservations may not always be accepted, waiting time may apply, and amenities may vary due to events, peak periods or refurbishment.</>,
    <><b>Domicile and residence rules:</b> Many clubs restrict or decline access to members who reside in the same city as the venue, or apply a minimum-distance rule between the member&apos;s residence and the club. Access may therefore be limited or unavailable based on your place of residence, at the host venue&apos;s discretion.</>,
    <><b>Golf clubs:</b> access requires an active Handicap Index — provide it when requesting access and carry official handicap proof on the day.</>,
    <>Cover charges, minimum spends or other fees, where applicable, are payable directly at the venue, and members and accompanying guests may be required to carry valid government-issued photo identification and physical membership cards (where applicable) for verification.</>,
    <>Each participating venue may be requested once per calendar month; members do not contact venues directly, and all visits are arranged through MindEscapes<sup>®</sup>.</>,
    <>All activities undertaken by the member and any accompanying guests are at their own risk, and MindEscapes<sup>®</sup> and its associates, affiliates, partners and service providers shall not be liable for any loss, injury, damage or claim arising from such access or use.</>,
    <>The rules of the host venue apply at all times — including any terms, conditions or guidelines it publishes, whether on its premises, website or in any document or PDF — and prevail while on its premises; the MindEscapes<sup>®</sup> Club Rules apply across the network. Read the full Terms, Club Rules and Privacy Policy: <a href="https://mindescapes.in/files/others/MEClubMembershipRules.pdf" target="_blank" rel="noopener noreferrer" data-testid="access-note-rules-pdf">mindescapes.in/files/others/MEClubMembershipRules.pdf</a></>,
    <>All venue names, images, logos and trademarks are the property of their respective owners, are used for identification and informational purposes only, and no affiliation, endorsement or sponsorship is implied unless expressly stated.</>,
    <>This listing is independently maintained; if you are the venue owner and wish to claim or request removal of this listing, contact <a href="mailto:legal@mindescapes.in" data-testid="access-note-claim-email">legal@mindescapes.in</a>.</>,
  ];

  return (
    <div className="access-note" data-testid={isGolf ? "access-note-golf" : "access-note-default"}>
      <h4>Important Access Note</h4>
      <ul>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </div>
  );
}

/* ─── Detail modal ────────────────────────────────────── */
function ClubDetail({ club, onClose, onRequest }) {
  const [voiceLine, setVoiceLine] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pid = club?.id_short || club?.id;
      if (!pid || !club?.name) return;
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const params = new URLSearchParams({ name: club.name });
        const ed = club.long || club.short || "";
        if (ed) params.set("editorial", String(ed).slice(0, 1500));
        if (club.addr) params.set("neighborhood", club.addr);
        if (club.type) params.set("types_label", club.type);
        const r = await fetch(`${base}/api/concierge/voice/club/${encodeURIComponent(pid)}?${params.toString()}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.voice_line) setVoiceLine(j.voice_line);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [club?.id_short, club?.id, club?.name, club?.long, club?.short, club?.addr, club?.type]);
  return (
    <div className="overlay" onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}
         data-testid="club-detail-overlay">
      <div className="modal" data-testid="club-detail-modal">
        <div className="mhero">
          <div className="cmono">{monogram(club.name)}</div>
          {club.img && (
            <CldImg src={club.img} w={1200} eager alt={club.name}
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <button className="mclose" onClick={onClose} aria-label="Close" data-testid="club-detail-close">×</button>
          <div className="mtitle">
            <div className="mt-tag">{club.type}</div>
            <h2 className="serif">{club.name}</h2>
            <div className="mt-loc">◦ {locOf(club)}</div>
          </div>
        </div>
        <div className="mbody">
          {voiceLine && (
            <div style={{ marginBottom: 18 }}>
              <DrawerVoiceLine text={voiceLine} testid="club-detail-concierge-voice" />
            </div>
          )}
          {(club.long || club.short) && formatLongDescription(club.long || club.short)}
          <CanonicalAccessNote isGolf={String(club.type || "").toLowerCase() === "golf"} />
          {club.am && club.am.length > 0 && (
            <div className="msec">
              <h4>Amenities &amp; what you can arrange</h4>
              <div className="mam">
                {club.am.map((a) => <span className="amtag" key={a}>{a}</span>)}
              </div>
            </div>
          )}
          <div className="msec">
            <div className="minfo">
              {club.addr && (
                <div className="it" style={{ gridColumn: "1 / -1" }}>
                  <div className="k">Address</div>
                  <div className="v">{club.addr}</div>
                </div>
              )}
              {club.hours && (
                <div className="it">
                  <div className="k">Opens</div>
                  <div className="v">{club.hours}</div>
                </div>
              )}
              {club.dress && (
                <div className="it" style={{ gridColumn: "1 / -1" }}>
                  <div className="k">Dress code</div>
                  <div className="v">{club.dress}</div>
                </div>
              )}
            </div>
          </div>
          <div className="visit-block">
            <h3 className="serif">How your visit is arranged</h3>
            <div className="steps">
              <div className="step">
                <div className="sn">1</div>
                <p>Tap <b>Request a visit</b> — share a first choice plus two dates.</p>
              </div>
              <div className="step">
                <div className="sn">2</div>
                <p>Your Concierge<sup>®</sup> confirms availability with the club.</p>
              </div>
              <div className="step">
                <div className="sn">3</div>
                <p>A Letter of Introduction is issued to you.</p>
              </div>
              <div className="step">
                <div className="sn">4</div>
                <p>Present it on arrival — welcomed as a guest.</p>
              </div>
            </div>
          </div>
          <div className="mcta">
            <button className="btn btn-primary" data-testid="club-detail-request-btn"
                    onClick={() => onRequest(club.id_short)}>
              Request a visit
            </button>
            <button className="btn btn-ghost" onClick={onClose} data-testid="club-detail-keep-browsing">
              Keep browsing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Request form ────────────────────────────────────── */
function RequestForm({ club, member, onClose, onBack, onSubmitted }) {
  const isGolf = String(club.type || "").toLowerCase() === "golf";
  const [party, setParty] = useState({ spouse: false, children: 0 });
  const [services, setServices] = useState({}); // { amenity: { fieldKey: value } }
  const [otherText, setOtherText] = useState("");
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({}); // { [`${amenity}_${field}`]: true }
  const [termsErr, setTermsErr] = useState(false);
  const [result, setResult] = useState(null); // confirmation payload
  // FIR-20260218-Golf · golf-specific intake (founder ruling 18 Feb 2026).
  // Handicap Index required; proof photo optional (member can also show
  // the physical card at the club). Filename only is captured for now —
  // upload-to-server is a separate flow that the desk handles.
  const [handicap, setHandicap] = useState("");
  const [handicapProof, setHandicapProof] = useState(null);
  const [handicapErr, setHandicapErr] = useState(false);
  const TERMS_VERSION = "2026-02-18";

  const total = partyTotal(party);

  const availableServices = useMemo(
    () => (club.am || []).filter((a) => SERVICE_FORMS[a]),
    [club.am]
  );

  function toggleService(a) {
    setServices((s) => {
      const next = { ...s };
      if (next[a]) delete next[a]; else next[a] = {};
      return next;
    });
  }

  function setField(a, k, v) {
    setServices((s) => ({ ...s, [a]: { ...(s[a] || {}), [k]: v } }));
    setErrors((e) => {
      if (!e[`${a}_${k}`]) return e;
      const c = { ...e }; delete c[`${a}_${k}`]; return c;
    });
  }

  function kids(d) {
    setParty((p) => ({ ...p, children: Math.max(0, Math.min(2, (p.children || 0) + d)) }));
  }

  async function submit() {
    const chosen = Object.keys(services);
    const other = (otherText || "").trim();
    if (chosen.length === 0 && !other) {
      alert("Please select at least one thing you'd like to arrange, or add a note under \"Anything else?\".");
      return;
    }
    // Validate required fields
    const newErrors = {};
    let ok = true;
    chosen.forEach((a) => {
      const cfg = SERVICE_FORMS[a];
      cfg.fields.forEach((f) => {
        if (f.req && !((services[a] || {})[f.k])) {
          newErrors[`${a}_${f.k}`] = true;
          ok = false;
        }
      });
    });
    // Golf-only: Handicap Index required.
    if (isGolf) {
      const h = String(handicap || "").trim();
      const n = parseFloat(h);
      if (!h || Number.isNaN(n) || n < 0 || n > 54) {
        setHandicapErr(true); ok = false;
      } else {
        setHandicapErr(false);
      }
    }
    if (!terms) { setTermsErr(true); ok = false; }
    setErrors(newErrors);
    if (!ok) return;

    setSubmitting(true);
    try {
      const payload = {
        card_no: member.card_no,
        club_id: club.id_short,
        party: { spouse: party.spouse, children: party.children, guests: 0 },
        services: chosen.map((a) => ({ service: a, details: services[a] || {} })),
        other: other || null,
        terms_accepted: true,
        terms_version: TERMS_VERSION,
        terms_agreed_at: new Date().toISOString(),
        // Golf-only fields (null for non-golf so backend / Zoho schema
        // is consistent across both paths).
        handicap_index: isGolf ? parseFloat(handicap) : null,
        handicap_proof_filename: isGolf && handicapProof ? handicapProof.name : null,
      };
      const r = await api.submitRequest(payload);
      setResult({ ...r, club: club.name, city: club.city, party, services, total });
    } catch (e) {
      alert("We couldn't send the request. Please try again. (" + (e.message || "unknown") + ")");
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    const serviceLines = Object.entries(services).map(([a, d]) => (
      <li key={a}>
        <span>{SERVICE_FORMS[a].label}</span>
        <span>{d.date || d.checkin || "requested"}</span>
      </li>
    ));
    return (
      <div className="overlay" data-testid="club-confirm-overlay">
        <div className="modal" data-testid="club-confirm-modal">
          <div className="mhero compact">
            <div className="cmono">{monogram(club.name)}</div>
            {club.img && <CldImg src={club.img} w={800} alt={club.name} onError={(e) => e.currentTarget.style.display = "none"} />}
            <button className="mclose" onClick={onClose}>×</button>
            <div className="mtitle">
              <div className="mt-tag">Request received</div>
              <h2 className="serif compact">{club.name}</h2>
            </div>
          </div>
          <div className="mbody">
            <div className="success">
              <div className="ic">✓</div>
              <h3 className="serif">Request received</h3>
              <p>
                Thank you, {member.first}. Our MindEscapes<sup>®</sup> team will revert to you on your request for your visit to{" "}
                <b>{club.name}, {club.city}</b> shortly.
              </p>
              <ul>
                <li><span>Club</span><span>{club.name}</span></li>
                <li><span>Party</span><span>{total}{total === 1 ? " person" : " people"}</span></li>
                {serviceLines}
              </ul>
              <div className="ref" data-testid="club-confirm-ref">Reference {result.ref}</div>
            </div>
            <div className="mcta">
              <button className="btn btn-primary" onClick={onClose} data-testid="club-confirm-done">
                Continue browsing
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay" onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}
         data-testid="club-request-overlay">
      <div className="modal" data-testid="club-request-modal">
        <div className="mhero compact">
          <div className="cmono">{monogram(club.name)}</div>
          {club.img && <CldImg src={club.img} w={800} alt={club.name} onError={(e) => e.currentTarget.style.display = "none"} />}
          <button className="mclose" onClick={onClose} aria-label="Close">×</button>
          <div className="mtitle">
            <div className="mt-tag">Request a visit</div>
            <h2 className="serif compact">{club.name}</h2>
          </div>
        </div>
        <div className="mbody">
          <div className="form-wrap">
            <div className="memberbox">
              <div className="av serif">{(member.first?.[0] || "M").toUpperCase()}</div>
              <div className="mi">
                <small>Member</small>
                <b>{member.first} {member.last}</b>
                <span className="card">{member.card_no}</span>
              </div>
            </div>

            <div className="blk">
              <span className="lab">Who is visiting?</span>
              <div className="party-rows">
                <div className="party-row party-fixed" data-testid="club-party-member">
                  <span className="x on">✓</span>
                  <span className="party-lbl">{member.first} (member)</span>
                </div>
                {/* FIR-20260612-010 · only show spouse / children rows when
                     the member's profile actually has them registered. The
                     backend now exposes `has_spouse` and `has_children` on
                     the member projection. */}
                {member.has_spouse && (
                  <div className={`party-row party-toggle${party.spouse ? " on" : ""}`}
                       onClick={() => setParty((p) => ({ ...p, spouse: !p.spouse }))}
                       role="button" tabIndex={0}
                       onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setParty((p) => ({ ...p, spouse: !p.spouse })); }}
                       data-testid="club-party-spouse">
                    <span className={`x${party.spouse ? " on" : ""}`}>{party.spouse ? "✓" : ""}</span>
                    <span className="party-lbl">
                      Spouse{member.spouse_name ? <small style={{ marginLeft: 6, opacity: 0.7 }}>{member.spouse_name}</small> : null}
                    </span>
                  </div>
                )}
                {member.has_children && (
                  <div className="party-row party-stepper-row">
                    <span className="party-lbl">Dependent children
                      <small> ({member.children_count} registered · max 2)</small>
                    </span>
                    <span className="stepper">
                      <button type="button" onClick={() => kids(-1)}
                              disabled={party.children <= 0}
                              data-testid="club-party-kid-minus">−</button>
                      <span className="val" data-testid="club-party-kid-val">{party.children}</span>
                      <button type="button" onClick={() => kids(1)}
                              disabled={party.children >= Math.min(2, member.children_count || 2)}
                              data-testid="club-party-kid-plus">+</button>
                    </span>
                  </div>
                )}
              </div>
              <div className="noguest">● No guests permitted — MindEscapes<sup>®</sup> access is for member, spouse &amp; dependent children only.</div>
              <div className="partytotal">Party size: <b>{total}{total === 1 ? " person" : " people"}</b></div>
            </div>

            <div className="blk">
              <span className="lab">What would you like to arrange?</span>
              <div className="svc-chips">
                {availableServices.map((a) => {
                  const cfg = SERVICE_FORMS[a];
                  const on = !!services[a];
                  return (
                    <button key={a} type="button"
                            className={`svc-chip${on ? " on" : ""}`}
                            data-testid={`club-svc-chip-${a.replace(/[^a-z]/gi, "")}`}
                            onClick={() => toggleService(a)}>
                      <span className="i">{cfg.icon}</span>{cfg.label}
                    </button>
                  );
                })}
              </div>
              {Object.keys(services).map((a) => {
                const cfg = SERVICE_FORMS[a];
                return (
                  <div className="svc-module" key={a}>
                    <h5 className="serif">{cfg.icon} {cfg.label}</h5>
                    <div className="fgrid">
                      {cfg.fields.map((f) => {
                        const errKey = `${a}_${f.k}`;
                        const isErr = !!errors[errKey];
                        const val = (services[a] || {})[f.k] || "";
                        const id = `svc_${a.replace(/[^a-z]/gi, "")}_${f.k}`;
                        return (
                          <div className={`field${f.full ? " full" : ""}${isErr ? " err" : ""}`} key={f.k}>
                            <label htmlFor={id}>
                              {f.l}{f.req && <span className="req"> *</span>}
                            </label>
                            {f.t === "select" ? (
                              <select id={id} value={val}
                                      onChange={(e) => setField(a, f.k, e.target.value)}
                                      data-testid={`club-svc-${id}`}>
                                <option value=""></option>
                                {f.o.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : f.t === "slot" ? (
                              <select id={id} value={val}
                                      onChange={(e) => setField(a, f.k, e.target.value)}
                                      data-testid={`club-svc-${id}`}>
                                <option value="">Select a time…</option>
                                {f.o.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            ) : f.t === "textarea" ? (
                              <textarea id={id} value={val}
                                        onChange={(e) => setField(a, f.k, e.target.value)}
                                        placeholder="Optional"
                                        data-testid={`club-svc-${id}`} />
                            ) : (
                              <input id={id} type={f.t} value={val}
                                     onChange={(e) => setField(a, f.k, e.target.value)}
                                     data-testid={`club-svc-${id}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="blk">
              <span className="lab">Anything else?</span>
              <div className="field full">
                <textarea value={otherText}
                          onChange={(e) => setOtherText(e.target.value)}
                          placeholder="Any other request — a specific facility, an occasion, timings, or something not listed above…"
                          data-testid="club-other-text" />
              </div>
            </div>

            {isGolf && (
              <div className="blk" data-testid="club-handicap-block">
                <span className="lab">Your Handicap Index <span className="req">*</span></span>
                <div className="fgrid">
                  <div className={`field${handicapErr ? " err" : ""}`}>
                    <label htmlFor="club-handicap-index">Handicap Index <span className="req">*</span></label>
                    <input
                      id="club-handicap-index"
                      type="number"
                      step="0.1"
                      min="0"
                      max="54"
                      placeholder="e.g. 18.4"
                      value={handicap}
                      onChange={(e) => { setHandicap(e.target.value); setHandicapErr(false); }}
                      data-testid="club-handicap-input"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="club-handicap-proof">
                      Handicap proof <small style={{ opacity: 0.7 }}>(optional · you can also show the card at the club)</small>
                    </label>
                    <input
                      id="club-handicap-proof"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setHandicapProof(e.target.files?.[0] || null)}
                      data-testid="club-handicap-proof"
                    />
                  </div>
                </div>
                {handicapErr && (
                  <p style={{ fontSize: 12, color: "#a23b3b", marginTop: 6 }}>
                    Please enter a valid Handicap Index (0–54).
                  </p>
                )}
              </div>
            )}

            <div className={`termsbox${terms ? " on" : ""}${termsErr ? " err" : ""}`}
                 onClick={() => { setTerms((t) => !t); setTermsErr(false); }}
                 data-testid="club-terms-box">
              <span className="cb">✓</span>
              <div className="terms-body">
                <p>
                  As a MindEscapes<sup>®</sup> member, I understand that this access is
                  made available through MindEscapes<sup>®</sup>&apos;s reciprocal
                  understandings, partner-club networks, third-party arrangements, or
                  venues whose select amenities are open to guests. Every visit is
                  subject to the host club&apos;s rules, availability and discretion on
                  the day. Each participating club may be visited once per calendar
                  month, and I will not contact any club or venue directly — all visits
                  are arranged through MindEscapes<sup>®</sup>.
                </p>
                <p>
                  I accept that all visits are undertaken at my own risk and that the
                  MindEscapes<sup>®</sup> Club Rules apply across the network —{" "}
                  <a
                    href="https://mindescapes.in/files/others/MEClubMembershipRules.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="club-terms-pdf-link"
                  >read the full terms</a>.
                </p>
                {isGolf && (
                  <p>
                    I confirm I have an active Handicap Index and will carry official
                    proof at the time of play.
                  </p>
                )}
                <p>
                  I confirm this request is for the <b>member, spouse and dependent
                  children only (no guests)</b>.
                </p>
              </div>
            </div>

            <div className="mcta">
              <button className="btn btn-primary" onClick={submit} disabled={submitting}
                      data-testid="club-request-submit">
                {submitting ? "Sending…" : "Send my request"}
              </button>
              <button className="btn btn-ghost" onClick={onBack} data-testid="club-request-back">
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────── */
export default function ClubDirectory() {
  const [config, setConfig] = useState(null);
  const [member, setMember] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [typeFilters, setTypeFilters] = useState(new Set());
  const [amFilters, setAmFilters] = useState(new Set());
  const [sortKey, setSortKey] = useState("city");
  const [limit, setLimit] = useState(PAGE_SIZE);

  const [openId, setOpenId] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [activeClub, setActiveClub] = useState(null);
  const [pillPulse, setPillPulse] = useState(false);
  const filterBlockRef = React.useRef(null);

  // Count of active filters (country + each type + each amenity)
  const activeFilterCount = (country ? 1 : 0) + typeFilters.size + amFilters.size;

  function triggerPillPulse() {
    setPillPulse(false);
    // re-arm on next tick so the CSS animation replays
    setTimeout(() => setPillPulse(true), 0);
    setTimeout(() => setPillPulse(false), 600);
  }

  function scrollToFilters() {
    const el = filterBlockRef.current;
    if (!el) return;
    // Manually offset by the combined sticky height (header 84 + search ~80 = 170 px desktop,
    // 72 + 70 = 142 px mobile). 24px breathing room below.
    const isMobile = window.innerWidth < 760;
    const offset = isMobile ? 142 : 170;
    const top = el.getBoundingClientRect().top + window.scrollY - offset - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }

  // 1) Resolve tenant config; if gate-on tenant, hydrate session or show gate.
  //
  // FIR-20260623-StuckLoading · the prior version had no timeout / retry on
  // getConfig(). If the browser ever stalled the request (CDN edge, ad-blocker
  // intercepting /api/*, corporate proxy), config stayed null forever and the
  // page showed "Loading…" indefinitely with no recourse. We now race the
  // fetch against an 8-second timeout; on failure we surface a friendly error
  // banner so the user knows to refresh / check their connection.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const cfg = await Promise.race([
          api.getConfig(),
          new Promise((_, rej) => setTimeout(() => rej(new Error("connect_timeout")), 8000)),
        ]);
        if (aborted) return;
        setConfig(cfg);
        if (cfg.gate_enabled) {
          const existing = loadSession();
          // FIR-20260623-StaleSession · validate the shape of any restored
          // session before trusting it. Older builds saved partial objects
          // that resulted in a "Signed in as undefined undefined" header.
          if (existing && existing.card_no && existing.last_name) {
            setMember(existing);
          } else if (existing) {
            // Corrupt cache — drop it silently and prompt the gate.
            clearSession();
          }
        } else {
          // Gate off (founder review surface): open as founder.
          const r = await api.authMember({});
          if (!aborted) setMember(r.member);
        }
      } catch (e) {
        if (!aborted) setAuthError(e.message || "connect_failed");
      }
    })();
    return () => { aborted = true; };
  }, []);

  function handleAuthed(authedMember) {
    saveSession(authedMember);
    setMember(authedMember);
  }
  function handleSignOut() {
    clearSession();
    setMember(null);
    // FIR-20260625-SignOutFix · on gate-off (founder) surfaces, clearing
    // the member would leave the user on an empty screen with no way back
    // in (the auto-auth useEffect only runs once at mount). Re-fetch the
    // founder session so sign-out behaves as a "session refresh" instead
    // of a dead end. On gate-on tenants, leaving member null is correct —
    // the ClubGate will render and prompt for credentials.
    if (config && !config.gate_enabled) {
      (async () => {
        try {
          const r = await api.authMember({});
          if (r && r.member) setMember(r.member);
        } catch { /* ignore — user can refresh */ }
      })();
    }
  }

  // 2) Fetch the catalogue once we have a member. Server applies §6/§6.5;
  //    for see_all (founder) we get ALL offerable clubs, so we filter
  //    client-side to keep search snappy.
  useEffect(() => {
    if (!member) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.listClubs({ member: member.card_no, limit: 5000 });
        setItems(r.items || []);
        setTotal(r.total || 0);
      } catch (e) {
        // FIR-20260623-StaleSession · a 401 here almost always means our
        // stored card_no is no longer valid (member deactivated, card
        // renamed by admin, prod DB rotated). Drop the session and re-
        // prompt the gate instead of rendering a ghost directory.
        if (e && e.status === 401) {
          clearSession();
          setMember(null);
        }
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [member]);

  const countries = useMemo(() => {
    const cc = {};
    items.forEach((c) => { cc[c.country] = (cc[c.country] || 0) + 1; });
    return Object.keys(cc)
      .sort((a, b) => (a || "").localeCompare(b || ""))
      .map((k) => ({ name: k, count: cc[k] }));
  }, [items]);

  const cityCount = useMemo(
    () => new Set(items.map((c) => c.city_normalised || c.city)).size,
    [items]
  );

  const filtered = useMemo(() => {
    // Normalise the query for resilient matching:
    //   · lowercase + trim (caps/small parity)
    //   · NFD strip diacritics (café → cafe)
    //   · drop leading articles "the/a/an "
    //   · tokenize on whitespace → AND-match every token across the haystack
    //   · drop stop-words ("yacht club IN Jakarta" should work like "yacht jakarta")
    //   · include `type` and `am` (amenities) in haystack so chip values are searchable
    const STOPWORDS = new Set(["in", "at", "of", "on", "near", "by", "the", "a", "an", "to", "and", "or"]);
    const stripDiacritics = (s) =>
      (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const normalise = (s) =>
      stripDiacritics((s || "").toLowerCase())
        .replace(/^(the|a|an)\s+/i, "")
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    const tokens = normalise(q)
      .split(" ")
      .filter((t) => t && !STOPWORDS.has(t));

    let r = items.filter((c) => {
      if (country && c.country !== country) return false;
      if (tokens.length) {
        const h = normalise(
          `${c.name || ""} ${c.city || ""} ${c.state || ""} ${c.country || ""} ${c.type || ""} ${(c.am || []).join(" ")}`
        );
        for (const t of tokens) {
          if (!h.includes(t)) return false;
        }
      }
      if (typeFilters.size > 0 && !typeFilters.has(c.type)) return false;
      if (amFilters.size > 0) {
        for (const a of amFilters) {
          if (!(c.am || []).includes(a)) return false;
        }
      }
      return true;
    });
    r.sort((a, b) => {
      if (sortKey === "name") return a.name.localeCompare(b.name);
      if (sortKey === "type") return (a.type || "").localeCompare(b.type || "") || (a.city || "").localeCompare(b.city || "");
      return (a.city || "").localeCompare(b.city || "") || (a.name || "").localeCompare(b.name || "");
    });
    return r;
  }, [items, q, country, typeFilters, amFilters, sortKey]);

  const visible = filtered.slice(0, limit);

  function toggleSetVal(setter, value) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
    setLimit(PAGE_SIZE);
    triggerPillPulse();
  }

  function clearAll() {
    setQ(""); setCountry("");
    setTypeFilters(new Set()); setAmFilters(new Set());
    setLimit(PAGE_SIZE);
  }

  async function openDetail(idShort) {
    setActiveClub(null);
    setOpenId(idShort);
    setRequestId(null);
    try {
      const r = await api.getClub(idShort, member.card_no);
      setActiveClub(r.club);
    } catch (e) {
      setActiveClub(null);
      setOpenId(null);
    }
  }

  function closeAll() {
    setOpenId(null);
    setRequestId(null);
    setActiveClub(null);
  }

  function goToRequest(idShort) {
    setRequestId(idShort);
  }

  // Gate-on tenant + no validated session yet → render the login gate.
  if (config && config.gate_enabled && !member) {
    return <ClubGate tenantName={config.name} onAuthed={handleAuthed} allowSignup={!!config.allow_signup} />;
  }

  // FIR-20260623-StuckLoading · if config failed to fetch (timeout, blocked
  // by extension, network down), surface a recoverable error instead of an
  // infinite "Loading…" spinner so the user can refresh or troubleshoot.
  if (!config && authError) {
    return (
      <ClubLayout member={null}>
        <main className="wrap">
          <div className="loading" data-testid="club-bootstrap-error"
               style={{ textAlign: "center", padding: "60px 20px" }}>
            <p style={{ fontSize: 18, marginBottom: 8 }}>Trouble connecting</p>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 20 }}>
              We couldn&apos;t reach the club network. Please check your connection and refresh.
            </p>
            <button type="button"
                    onClick={() => { try { window.location.reload(); } catch { /* noop */ } }}
                    data-testid="club-bootstrap-retry"
                    style={{ padding: "10px 24px", border: "1px solid var(--teal)",
                             borderRadius: 24, background: "var(--teal)", color: "#fff",
                             fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
              Refresh
            </button>
          </div>
        </main>
      </ClubLayout>
    );
  }

  // Still loading config (or auth-off mode loading the founder)
  if (!member && !authError) {
    return (
      <ClubLayout member={null}>
        <main className="wrap"><div className="loading" data-testid="club-bootstrap-loading">Loading…</div></main>
      </ClubLayout>
    );
  }

  // FIR-20260625-SignOutFix · founder reported the sign-out button was
  // never visible on the gate-off (`global`) surface. Previously gated on
  // `config.gate_enabled` which left auto-authed founders stuck signed in
  // with no exit. Now the button shows whenever a `member` object is
  // mounted — sign-out is always reachable.
  const showSignOut = !!member;

  return (
    <ClubLayout member={member} onSignOut={showSignOut ? handleSignOut : undefined}>
      <main className="wrap">
        <section className="hero">
          <div className="kicker">MindEscapes<sup>®</sup> · Club Discovery Network</div>
          <h1 className="serif">
            One Card. <em>One Concierge®.</em><br />
            A World of Clubs.
          </h1>
          <p>
            Browse our worldwide network of clubs and tell us where you&apos;d like to visit.
            A trained Concierge® will arrange the details — quietly, completely.
          </p>
          <div className="stats" data-testid="club-stats">
            <div>
              <b className="serif" data-testid="club-stat-clubs">{loading ? "…" : items.length.toLocaleString()}</b>
              <span>Clubs listed</span>
            </div>
            <div>
              <b className="serif" data-testid="club-stat-cities">{loading ? "…" : cityCount.toLocaleString()}</b>
              <span>Cities</span>
            </div>
            <div>
              <b className="serif" data-testid="club-stat-countries">{loading ? "…" : countries.length.toLocaleString()}</b>
              <span>Countries</span>
            </div>
          </div>
        </section>

        <section className="search-sticky" data-testid="club-search-sticky">
          <div className="search-row">
            <div className="search">
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search a club, city, country…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setLimit(PAGE_SIZE); }}
                data-testid="club-search-input"
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button"
                className={`filters-pill${pillPulse ? " pulse" : ""}`}
                onClick={scrollToFilters}
                data-testid="club-filters-pill"
                aria-label={`${activeFilterCount} active filter${activeFilterCount === 1 ? "" : "s"} — tap to view`}>
                <span className="pill-label">Filters</span>
                <span className="pill-num" data-testid="club-filters-pill-count">{activeFilterCount}</span>
                <span className="pill-arrow">↑</span>
              </button>
            )}
          </div>
        </section>

        <section className="filter-block" data-testid="club-filter-block" ref={filterBlockRef}>
          <div className="filters">
            <div className="frow">
              <span className="flabel">Country</span>
              <select className="country-sel"
                      value={country}
                      onChange={(e) => { setCountry(e.target.value); setLimit(PAGE_SIZE); triggerPillPulse(); }}
                      data-testid="club-country-select">
                <option value="">All countries ({items.length.toLocaleString()})</option>
                {countries.map((c) => (
                  <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
                ))}
              </select>
            </div>
            <div className="frow">
              <span className="flabel">Type</span>
              {TYPES.filter((t) => items.some((c) => c.type === t)).map((t) => (
                <button key={t}
                        className={`chip${typeFilters.has(t) ? " on" : ""}`}
                        onClick={() => toggleSetVal(setTypeFilters, t)}
                        data-testid={`club-chip-type-${t.replace(/[^a-z]/gi, "")}`}>
                  <span className="dot" />{t}
                </button>
              ))}
            </div>
            <div className="frow">
              <span className="flabel">Amenity</span>
              {AMENITIES.filter((a) => items.some((c) => (c.am || []).includes(a))).map((a) => (
                <button key={a}
                        className={`chip am${amFilters.has(a) ? " on" : ""}`}
                        onClick={() => toggleSetVal(setAmFilters, a)}
                        data-testid={`club-chip-am-${a.replace(/[^a-z]/gi, "")}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div className="metarow">
            <div className="count">
              <b data-testid="club-count">{filtered.length.toLocaleString()}</b> club{filtered.length === 1 ? "" : "s"} match
            </div>
            <div className="tools">
              <button className="clear-btn" onClick={clearAll} data-testid="club-clear-filters">Clear filters</button>
              <select className="sort-sel" value={sortKey}
                      onChange={(e) => setSortKey(e.target.value)}
                      data-testid="club-sort-select">
                <option value="city">Sort: City</option>
                <option value="name">Sort: Name</option>
                <option value="type">Sort: Type</option>
              </select>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="loading" data-testid="club-loading">Concierge is gathering the network…</div>
        ) : visible.length === 0 ? (
          <div className="empty" data-testid="club-empty">
            <div className="serif">No clubs match just yet.</div>
            Try removing a filter or searching a different city.
          </div>
        ) : (
          <div className="grid" data-testid="club-grid">
            {visible.map((c) => (
              <ClubCard key={c.id_short} club={c} onOpen={openDetail} />
            ))}
          </div>
        )}

        {filtered.length > visible.length && (
          <button className="loadmore" onClick={() => setLimit((l) => l + PAGE_SIZE)} data-testid="club-loadmore">
            Show more
          </button>
        )}

        {authError && (
          <div className="empty">
            <div className="serif">Sign-in error</div>
            {authError}
          </div>
        )}
      </main>

      {openId && activeClub && !requestId && (
        <ClubDetail club={activeClub} onClose={closeAll} onRequest={goToRequest} />
      )}
      {openId && activeClub && requestId && (
        <RequestForm
          club={activeClub}
          member={member}
          onClose={closeAll}
          onBack={() => setRequestId(null)}
        />
      )}
      <ConciergeWhatsAppPill context="club" />
    </ClubLayout>
  );
}
